export default class LowLatencySound {

    static sharedContext = null
    static unlockBound = false

    constructor(path) {
        this.path = path
        this.volume = 1
        this.buffer = null
        this.fallbackAudio = null
        this.currentSource = null
        this.readyPromise = this.load()
    }

    static getAudioContext() {
        if (typeof window === "undefined") {
            return null
        }

        const AudioContextClass = window.AudioContext || window.webkitAudioContext

        if (!AudioContextClass) {
            return null
        }

        if (!LowLatencySound.sharedContext) {
            LowLatencySound.sharedContext = new AudioContextClass()
        }

        if (!LowLatencySound.unlockBound) {
            const unlock = () => {
                if (LowLatencySound.sharedContext?.state === "suspended") {
                    LowLatencySound.sharedContext.resume().catch(() => {})
                }
            }

            window.addEventListener("pointerdown", unlock, { passive: true })
            window.addEventListener("keydown", unlock, { passive: true })
            LowLatencySound.unlockBound = true
        }

        return LowLatencySound.sharedContext
    }

    async load() {
        const audioContext = LowLatencySound.getAudioContext()

        if (!audioContext) {
            this.fallbackAudio = new window.Audio(this.path)
            this.fallbackAudio.preload = "auto"
            this.fallbackAudio.load()
            return
        }

        try {
            const response = await fetch(this.path)
            const arrayBuffer = await response.arrayBuffer()
            this.buffer = await audioContext.decodeAudioData(arrayBuffer)
        }
        catch {
            this.fallbackAudio = new window.Audio(this.path)
            this.fallbackAudio.preload = "auto"
            this.fallbackAudio.load()
        }
    }

    set currentTime(value) {
        if (this.fallbackAudio) {
            this.fallbackAudio.currentTime = value
        }
    }

    get currentTime() {
        if (this.fallbackAudio) {
            return this.fallbackAudio.currentTime
        }

        return 0
    }

    play() {
        const audioContext = LowLatencySound.getAudioContext()

        if (!audioContext || !this.buffer) {
            if (!this.fallbackAudio) {
                return Promise.resolve()
            }

            this.fallbackAudio.volume = this.volume
            this.fallbackAudio.currentTime = 0
            return this.fallbackAudio.play()
        }

        if (audioContext.state === "suspended") {
            audioContext.resume().catch(() => {})
        }

        if (this.currentSource) {
            this.currentSource.stop()
        }

        const source = audioContext.createBufferSource()
        const gainNode = audioContext.createGain()

        source.buffer = this.buffer
        gainNode.gain.value = this.volume

        source.connect(gainNode)
        gainNode.connect(audioContext.destination)
        source.start(0)

        this.currentSource = source
        source.onended = () => {
            if (this.currentSource === source) {
                this.currentSource = null
            }
        }

        return Promise.resolve()
    }
}
